/**
 * RhOrganigrammePanel — organigramme (5.13) et langues parlées (5.24).
 *
 * ─── 5.13 ───────────────────────────────────────────────────────────────────
 * Deux versions, comme demandé : anonyme (par fonction) et nominative.
 * L'anonyme est celle qui s'affiche par défaut — c'est celle qu'on publie, et
 * partir du plus protecteur évite qu'un écran ouvert sur un bureau expose des
 * noms à un administré de passage.
 *
 * ─── 5.24 ───────────────────────────────────────────────────────────────────
 * Les langues sont regroupées PAR LANGUE, pas par agent : le cas d'usage réel
 * est « un administré sourd se présente à l'accueil, qui peut aider ? ».
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Card, Badge, Alert, Button } from '@/components/ui';
import { Network, Languages, Loader2, AlertCircle, Info, BellRing } from 'lucide-react';

interface Noeud {
  id: string;
  nom: string | null;
  fonction: string | null;
  grade: string | null;
  service: string | null;
  subordonnes: Noeud[];
}

/** Un nœud et ses rattachements, en retrait. */
function Branche({ noeud, niveau }: { noeud: Noeud; niveau: number }) {
  return (
    <li>
      <div
        className="flex flex-wrap items-center gap-2 border-l border-border py-1.5 pl-3"
        style={{ marginLeft: niveau * 12 }}
      >
        <span className="text-sm text-ink">
          {noeud.fonction ?? 'Fonction non précisée'}
        </span>
        {noeud.nom && (
          <span className="text-xs text-ink-muted">{noeud.nom}</span>
        )}
        {noeud.grade && <Badge variant="neutral" size="sm">{noeud.grade}</Badge>}
        {noeud.service && (
          <span className="text-2xs text-ink-subtle">{noeud.service}</span>
        )}
      </div>
      {noeud.subordonnes.length > 0 && (
        <ul>
          {noeud.subordonnes.map((s) => (
            <Branche key={s.id} noeud={s} niveau={niveau + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

export function RhOrganigrammePanel() {
  const { user } = useAuth();
  const peutVoirNoms = ['super_admin', 'admin', 'manager'].includes(user?.role ?? '');

  // Par défaut anonyme : on part du plus protecteur.
  const [anonyme, setAnonyme] = useState(true);
  const [racines, setRacines] = useState<Noeud[]>([]);
  const [effectifs, setEffectifs] = useState<Array<{ service: string; effectif: number }>>([]);
  const [orphelins, setOrphelins] = useState(0);
  const [langues, setLangues] = useState<Array<{
    langue: string; agents: Array<{ nom: string; service: string | null }>;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [o, l] = await Promise.all([
        api.rh.organigramme(anonyme),
        api.rh.langues(),
      ]);
      setRacines(o.racines as Noeud[]);
      setEffectifs(o.effectifsParService);
      setOrphelins(o.orphelins);
      setLangues(l.langues);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Chargement impossible');
    } finally { setLoading(false); }
  }, [anonyme]);

  useEffect(() => { void charger(); }, [charger]);

  if (loading) {
    return (
      <Card variant="glass" padded>
        <p className="flex items-center gap-2 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Chargement…
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}

      {/* ── Délais de relance (5.16 / 5.17 / 5.19) ── */}
      {peutVoirNoms && <DelaisRelancePanel />}

      {/* ── Organigramme (5.13) ── */}
      <Card variant="glass" padded>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-1.5 text-sm font-medium text-ink">
            <Network className="h-4 w-4" aria-hidden="true" /> Organigramme
          </h3>
          {peutVoirNoms && (
            <div className="flex items-center gap-1">
              <Button
                variant={anonyme ? 'primary' : 'ghost'} size="sm"
                onClick={() => setAnonyme(true)}
              >
                Par fonction
              </Button>
              <Button
                variant={!anonyme ? 'primary' : 'ghost'} size="sm"
                onClick={() => setAnonyme(false)}
              >
                Nominatif
              </Button>
            </div>
          )}
        </div>

        <Alert variant="info" icon={Info} className="mb-3">
          Déduit des dossiers agents — service, fonction et rattachement
          hiérarchique. Rien à saisir ici&nbsp;: il se met à jour tout seul.
          {!anonyme && (
            <span className="mt-1 block text-2xs">
              Version nominative&nbsp;: à ne pas diffuser hors de la mairie.
            </span>
          )}
        </Alert>

        {orphelins > 0 && (
          <Alert variant="warning" icon={AlertCircle} className="mb-3">
            {orphelins} agent{orphelins > 1 ? 's' : ''} dont le responsable n'est
            plus actif — ils apparaissent à la racine, leur rattachement est à
            refaire dans leur dossier.
          </Alert>
        )}

        {racines.length === 0 ? (
          <p className="py-4 text-center text-sm text-ink-muted">
            Aucun dossier agent actif.
          </p>
        ) : (
          <ul className="text-sm">
            {racines.map((n) => <Branche key={n.id} noeud={n} niveau={0} />)}
          </ul>
        )}

        {effectifs.length > 0 && (
          <div className="mt-4 border-t border-border pt-3">
            <p className="mb-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
              Effectifs par service
            </p>
            <div className="flex flex-wrap gap-1.5">
              {effectifs.map((e) => (
                <span key={e.service}
                  className="inline-flex items-center gap-1.5 rounded-r-md border border-border bg-surface/40 px-2 py-0.5 text-2xs text-ink-muted">
                  {e.service}
                  <Badge variant="info" size="sm">{e.effectif}</Badge>
                </span>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* ── Langues parlées (5.24) ── */}
      <Card variant="glass" padded>
        <h3 className="mb-2 flex items-center gap-1.5 text-sm font-medium text-ink">
          <Languages className="h-4 w-4" aria-hidden="true" /> Langues parlées
        </h3>
        <p className="mb-3 text-xs text-ink-muted">
          Qui peut aider quand un administré ne parle pas français, ou signe.
        </p>

        {langues.length === 0 ? (
          <p className="py-3 text-center text-sm text-ink-muted">
            Aucune langue renseignée dans les dossiers agents.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {langues.map((l) => (
              <li key={l.langue} className="py-2">
                <p className="text-sm font-medium text-ink">{l.langue}</p>
                <p className="mt-0.5 text-2xs text-ink-muted">
                  {l.agents.map((a) =>
                    a.service ? `${a.nom} (${a.service})` : a.nom,
                  ).join(' · ')}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/**
 * Délais de relance de la commune.
 *
 * Les colonnes existaient depuis la migration 238 avec leurs valeurs par
 * défaut, mais aucun écran ne permettait de les régler : le paramétrage était
 * réel en base et invisible dans l'application.
 *
 * Réglage de COMMUNE (décision du 25/07) : une RH veut être prévenue « deux
 * mois avant », partout — pas fixer soixante délais différents.
 */
function DelaisRelancePanel() {
  const [psc, setPsc] = useState<{ quantite: number; unite: string }>({ quantite: 3, unite: 'mois' });
  const [cnas, setCnas] = useState<{ quantite: number; unite: string }>({ quantite: 2, unite: 'mois' });
  const [sft, setSft] = useState<{ quantite: number; unite: string }>({ quantite: 2, unite: 'mois' });
  const [seuil, setSeuil] = useState(72);
  const [montant, setMontant] = useState('0');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const d = await api.rh.delaisRelance();
        // On n'expose qu'UN délai par famille à l'écran : la base en accepte
        // plusieurs, mais deux relances pour une même échéance sont surtout un
        // moyen de se faire ignorer.
        if (d.psc[0]) setPsc(d.psc[0]);
        if (d.cnas[0]) setCnas(d.cnas[0]);
        if (d.sft[0]) setSft(d.sft[0]);
        setSeuil(d.seuilRequalificationMois);
        setMontant((d.sftMontantParEnfantCents / 100).toFixed(2));
      } catch (e) {
        setErr(e instanceof ApiException ? e.payload.error : 'Chargement impossible');
      } finally { setLoading(false); }
    })();
  }, []);

  async function enregistrer() {
    setSaving(true); setErr(null); setMessage(null);
    try {
      await api.rh.majDelaisRelance({
        psc: [psc], cnas: [cnas], sft: [sft],
        seuilRequalificationMois: seuil,
        // Saisi en euros, stocké en centimes : jamais de flottant sur de l'argent.
        sftMontantParEnfantCents: Math.round(parseFloat(montant.replace(',', '.') || '0') * 100),
      });
      setMessage('Réglages enregistrés. Ils s\'appliquent aux prochaines échéances saisies.');
    } catch (e) {
      setErr(e instanceof ApiException ? e.payload.error : 'Enregistrement impossible');
    } finally { setSaving(false); }
  }

  if (loading) return null;

  const champ = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink focus:border-rh focus:outline-none focus:ring-2 focus:ring-rh';

  return (
    <Card variant="glass" padded>
      <h3 className="mb-1 flex items-center gap-1.5 text-sm font-medium text-ink">
        <BellRing className="h-4 w-4" aria-hidden="true" /> Délais de relance
      </h3>
      <p className="mb-3 text-xs text-ink-muted">
        Combien de temps avant l'échéance VPC doit vous prévenir. Ces réglages
        valent pour <strong>toute la commune</strong>.
      </p>

      {err && <Alert variant="error" icon={AlertCircle} className="mb-3">{err}</Alert>}
      {message && <Alert variant="success" icon={Info} className="mb-3">{message}</Alert>}

      <div className="space-y-3">
        {([
          ['Protection sociale (prévoyance et santé)', psc, setPsc],
          ['Adhésion CNAS', cnas, setCnas],
          ['Fin de droit au supplément familial', sft, setSft],
        ] as const).map(([libelle, valeur, setter]) => (
          <div key={libelle} className="flex flex-wrap items-center gap-2">
            <span className="min-w-[16rem] text-sm text-ink">{libelle}</span>
            <input type="number" min={1} max={365} value={valeur.quantite}
              onChange={(e) => setter({ ...valeur, quantite: Number(e.target.value) || 1 })}
              aria-label={`Délai ${libelle}`}
              className={`${champ} w-20`} />
            <select value={valeur.unite}
              onChange={(e) => setter({ ...valeur, unite: e.target.value })}
              aria-label={`Unité ${libelle}`}
              className={`${champ} w-36`}>
              <option value="jours">jour(s)</option>
              <option value="semaines">semaine(s)</option>
              <option value="mois">mois</option>
              <option value="annees">année(s)</option>
            </select>
            <span className="text-xs text-ink-muted">avant l'échéance</span>
          </div>
        ))}

        <div className="border-t border-border pt-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="rh-seuil" className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                Seuil d'alerte de requalification CDD (mois)
              </label>
              <input id="rh-seuil" type="number" min={6} max={240} value={seuil}
                onChange={(e) => setSeuil(Number(e.target.value) || 72)} className={`${champ} mt-1`} />
              <p className="mt-1 text-2xs text-ink-muted">
                VPC signale, il ne requalifie pas : la décision reste juridique.
              </p>
            </div>
            <div>
              <label htmlFor="rh-sft" className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                Montant du SFT par enfant (€ / mois)
              </label>
              <input id="rh-sft" type="text" inputMode="decimal" value={montant}
                onChange={(e) => setMontant(e.target.value)} className={`${champ} mt-1`} />
              <p className="mt-1 text-2xs text-ink-muted">
                0 = non paramétré : aucun montant ne s'affiche sur les dossiers.
              </p>
            </div>
          </div>
        </div>

        <Button variant="primary" size="sm" loading={saving} onClick={() => void enregistrer()}>
          Enregistrer les réglages
        </Button>
      </div>
    </Card>
  );
}
